import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { SceneBase } from './SceneBase';
import { Balloon } from '../atoms/kids/Balloon';
import { BallPitBalls } from '../atoms/kids/BallPitBalls';
import { Stars } from '../atoms/kids/Stars';
import { bounce, bounceReduced, float } from '../../../../../theme/animations';

/**
 * KidsScene - Card background for kids play area venue type.
 *
 * Features a warm orange gradient, confetti-like scattered dots,
 * and playful decorative atoms. Hover reveals more confetti
 * and adds a gentle bounce to elements.
 */
export const KidsScene = React.memo(function KidsScene() {
  const theme = useTheme();
  const kidsColor = theme.palette.venue.kids;

  return (
    <SceneBase>
      {({ isMobile, shouldReduceMotion }) => (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            // Warm gradient background
            background: `linear-gradient(135deg, ${kidsColor}1F 0%, ${kidsColor}0A 100%)`,
            // Confetti-like scattered dots using CSS radial gradients
            backgroundImage: `
              linear-gradient(135deg, ${kidsColor}1F 0%, ${kidsColor}0A 100%),
              radial-gradient(circle 2px at 15% 20%, ${kidsColor}1A 50%, transparent 50%),
              radial-gradient(circle 1.5px at 45% 10%, ${kidsColor}15 50%, transparent 50%),
              radial-gradient(circle 2px at 75% 25%, ${kidsColor}1A 50%, transparent 50%),
              radial-gradient(circle 1px at 25% 70%, ${kidsColor}12 50%, transparent 50%),
              radial-gradient(circle 2.5px at 85% 60%, ${kidsColor}15 50%, transparent 50%),
              radial-gradient(circle 1.5px at 55% 80%, ${kidsColor}12 50%, transparent 50%),
              radial-gradient(circle 1px at 35% 45%, ${kidsColor}10 50%, transparent 50%),
              radial-gradient(circle 2px at 65% 50%, ${kidsColor}12 50%, transparent 50%)
            `,
            // Soft inner glow
            boxShadow: `inset 0 0 25px ${kidsColor}0D`,
            // Hover intensification - CSS only, hover-capable devices only
            transition: 'box-shadow 0.3s ease, background 0.3s ease',
            '@media (hover: hover)': {
              '&:hover': {
                boxShadow: `inset 0 0 40px ${kidsColor}1A`,
                background: `linear-gradient(135deg, ${kidsColor}2B 0%, ${kidsColor}12 100%)`,
              },
            },
          }}
        >
          {/* Balloon atom - floating in upper area */}
          <motion.div
            variants={shouldReduceMotion ? bounceReduced : float}
            initial="hidden"
            animate="visible"
            style={{
              position: 'absolute',
              top: isMobile ? '5%' : '8%',
              right: isMobile ? '10%' : '15%',
              opacity: 0.3,
            }}
          >
            <Balloon size={isMobile ? 36 : 48} />
          </motion.div>

          {/* BallPitBalls atom - bottom area */}
          <motion.div
            variants={shouldReduceMotion ? bounceReduced : bounce}
            initial="hidden"
            animate="visible"
            style={{
              position: 'absolute',
              bottom: isMobile ? '5%' : '8%',
              left: isMobile ? '5%' : '8%',
              opacity: 0.25,
            }}
          >
            <BallPitBalls size={isMobile ? 44 : 60} />
          </motion.div>

          {/* Stars atom - scattered across the scene */}
          <motion.div
            variants={shouldReduceMotion ? bounceReduced : bounce}
            initial="hidden"
            animate="visible"
            style={{
              position: 'absolute',
              top: isMobile ? '40%' : '35%',
              left: isMobile ? '60%' : '55%',
              opacity: 0.2,
            }}
          >
            <Stars size={isMobile ? 28 : 40} />
          </motion.div>

          {/* Additional balloon - top left (desktop only) */}
          {!isMobile && (
            <motion.div
              variants={shouldReduceMotion ? bounceReduced : float}
              initial="hidden"
              animate="visible"
              style={{
                position: 'absolute',
                top: '12%',
                left: '10%',
                opacity: 0.18,
              }}
            >
              <Balloon size={30} />
            </motion.div>
          )}

          {/* Additional stars - bottom right (desktop only) */}
          {!isMobile && (
            <motion.div
              variants={shouldReduceMotion ? bounceReduced : bounce}
              initial="hidden"
              animate="visible"
              style={{
                position: 'absolute',
                bottom: '12%',
                right: '8%',
                opacity: 0.15,
              }}
            >
              <Stars size={24} />
            </motion.div>
          )}
        </Box>
      )}
    </SceneBase>
  );
});
